import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isMobile } from 'react-device-detect'
import { motion, MotionConfig } from 'motion/react'
import { keywordTags } from '../../constants/tags'
import CloseButton from '../CloseButton'
import KeywordsFilterFooter from './Footer'
import KeywordColumn from './KeywordColumn'
import { getFirstLetter, getLocalizedName } from './utils'
import { useCloudLayout } from './useCloudLayout'
import { closeButtonVariants } from '../TagFilter/filterAnimation'
import { overlayVariants } from './filterAnimation'
import { cn } from '../../utils/cn'

// The filter is laid out in rem, so the root font size scales it with the viewport while it is open
const rootFontSizeClassNames = [
  'text-[16px]',
  'max-sm:text-[14px]',
  'md:text-[16px]',
  'lg:text-[17px]',
  'xxl:text-[18px]',
]

const KeywordsFilterRootFontSize = () => {
  // a layout effect, so the font size is in place before the first paint and the tags don't reflow
  useLayoutEffect(() => {
    const root = document.documentElement
    root.classList.add(...rootFontSizeClassNames)
    return () => root.classList.remove(...rootFontSizeClassNames)
  }, [])

  return null
}

// Rendered inside AnimatePresence, so it stays until the overlay has faded out
const KeywordsFilterOverlay = (props: {
  onSelectKeyword: (keywordId: number) => void
  onClose: () => void
}) => {
  const { i18n } = useTranslation()
  const listRef = useRef<HTMLDivElement | null>(null)
  const dragStartXRef = useRef(0)
  const dragStartScrollLeftRef = useRef(0)
  const [isDragging, setIsDragging] = useState(false)

  const locale = i18n.language.startsWith('en') ? 'en' : 'hu'

  const visibleKeywords = useMemo(() => {
    return [...keywordTags]
      .filter(keyword => Number.isFinite(keyword.id))
      .sort((a, b) => {
        const nameA = getLocalizedName(a, i18n.language)
        const nameB = getLocalizedName(b, i18n.language)
        return nameA.localeCompare(nameB, locale, { sensitivity: 'base' })
      })
  }, [keywordTags, i18n.language, locale])

  const keywordCountRange = useMemo(() => {
    if (!visibleKeywords.length) {
      return { minCount: 0, maxCount: 0 }
    }

    let minCount = Number.POSITIVE_INFINITY
    let maxCount = Number.NEGATIVE_INFINITY

    visibleKeywords.forEach(keyword => {
      minCount = Math.min(minCount, keyword.count)
      maxCount = Math.max(maxCount, keyword.count)
    })

    return { minCount, maxCount }
  }, [visibleKeywords])

  const groupedKeywords = useMemo(() => {
    const grouped = visibleKeywords.reduce<Record<string, typeof visibleKeywords>>(
      (acc, keyword) => {
        const localizedName = getLocalizedName(keyword, i18n.language)
        const firstLetter = getFirstLetter(localizedName || '', i18n.language)

        if (!acc[firstLetter]) {
          acc[firstLetter] = []
        }

        acc[firstLetter].push(keyword)
        return acc
      },
      {},
    )

    return Object.entries(grouped).sort(([a], [b]) => {
      if (a === '#') return 1
      if (b === '#') return -1
      return a.localeCompare(b, locale, { sensitivity: 'base' })
    })
  }, [visibleKeywords, i18n.language, locale])

  const letters = useMemo(() => groupedKeywords.map(([letter]) => letter), [groupedKeywords])

  useCloudLayout(listRef, groupedKeywords)

  const handleWheel: React.WheelEventHandler<HTMLDivElement> = e => {
    const list = listRef.current
    if (!list) return

    if (Math.abs(e.deltaY) > 0) {
      e.preventDefault()
      list.scrollLeft += e.deltaY
    }
  }

  const handlePointerDown: React.PointerEventHandler<HTMLDivElement> = e => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    const list = listRef.current
    if (!list) return

    setIsDragging(true)
    dragStartXRef.current = e.clientX
    dragStartScrollLeftRef.current = list.scrollLeft
  }

  const handlePointerMove: React.PointerEventHandler<HTMLDivElement> = e => {
    if (!isDragging) return
    const list = listRef.current
    if (!list) return

    const delta = e.clientX - dragStartXRef.current
    list.scrollLeft = dragStartScrollLeftRef.current - delta
  }

  const stopDragging = () => {
    setIsDragging(false)
  }

  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const el = e.target as HTMLElement
    if (el.classList.contains('outside')) {
      props.onClose()
    }
  }

  return (
    <MotionConfig reducedMotion="user">
      <KeywordsFilterRootFontSize />
      <motion.div
        className="outside fixed inset-0 z-[2] flex items-center justify-center bg-black before:pointer-events-none before:fixed before:inset-0 before:z-0 before:bg-[rgba(0,0,0,0.8)] before:content-['']"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        onClick={handleOutsideClick}
      >
        <div className="relative z-[1] h-screen w-screen overflow-hidden rounded-none border-none bg-transparent text-white shadow-none">
          <div
            className={cn(
              'fixed left-0 top-0 z-[2] flex h-screen w-screen flex-row items-center gap-[12px] overflow-x-auto overflow-y-hidden bg-transparent bg-[length:10px_2px] bg-[position:left_calc(50%+(var(--list-pt)-var(--list-pb))/2)] bg-repeat-x px-[4rem] pb-[var(--list-pb)] pt-[var(--list-pt)] [--list-pt:6rem] [background-image:linear-gradient(_to_right,rgba(255,255,255,0.5)_0px,rgba(255,255,255,0.5)_2px,transparent_2px,transparent_10px_)] [scrollbar-width:none] max-sm:gap-[8px] max-sm:px-[32px] max-sm:[--list-pt:10px] [&::-webkit-scrollbar]:hidden',
              // the bottom padding keeps the tags clear of the footer: its track and the letters above it
              isMobile
                ? '[--list-pb:6rem] max-sm:[--list-pb:14px]'
                : '[--list-pb:10.5rem] max-sm:[--list-pb:7.5rem] xxl:[--list-pb:calc(6vh+70px+8.5rem)]',
              { 'cursor-grabbing': isDragging, 'cursor-grab': !isDragging },
            )}
            ref={listRef}
            onWheel={handleWheel}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={stopDragging}
            onPointerCancel={stopDragging}
            onPointerLeave={stopDragging}
          >
            {groupedKeywords.map(([letter, items], index) => (
              <KeywordColumn
                key={letter}
                index={index}
                letter={letter}
                items={items}
                language={i18n.language}
                minCount={keywordCountRange.minCount}
                maxCount={keywordCountRange.maxCount}
                onSelectKeyword={props.onSelectKeyword}
              />
            ))}
          </div>
          {!isMobile && (
            <KeywordsFilterFooter
              listRef={listRef}
              letters={letters}
              keywordCountRange={keywordCountRange}
            />
          )}
        </div>
      </motion.div>
      <CloseButton
        // the css transition would trail behind every frame of the animation
        className="[transition:none]"
        variants={closeButtonVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        onClick={props.onClose}
      />
    </MotionConfig>
  )
}

export default KeywordsFilterOverlay
